/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { combineReducers } from 'redux'

import type { StackVersionConfig } from '@modules/cloud-api/v1/types'
import type { RegionId, VersionNumber } from '@modules/ui-types'

import versions from './versions'
import whitelist from './whitelist'

import type { ReduxState } from '@/types/redux'
import type { VersionsState, WhitelistState } from './stackTypes'

export interface State {
  stackVersions: VersionsState
  whitelist: WhitelistState
}

// @ts-ignore
export default combineReducers<State>({ stackVersions: versions, whitelist })

export function _getVersionStacks(state: State, regionId: RegionId) {
  return state.stackVersions[regionId]
}

function _getVersionStack(
  state: State,
  regionId: RegionId,
  version: VersionNumber,
): StackVersionConfig | undefined {
  const allVersions = state.stackVersions[regionId] || []

  return allVersions.find((v) => v.version === version)
}

function _getActiveVersionStacks(state: State, regionId: RegionId) {
  return state.stackVersions[regionId]?.filter((version) => !version.deleted)
}

function _getVersionWhitelist(state: State, regionId: RegionId): string[] | undefined {
  return state.whitelist[regionId] ?? []
}

export const getActiveVersionStacks = (state: ReduxState, regionId: RegionId) =>
  _getActiveVersionStacks(state.versions, regionId)

export const getVersionStack = (state: ReduxState, regionId: RegionId, version: string) =>
  _getVersionStack(state.versions, regionId, version)

export const getVersionStacks = (state: ReduxState, regionId: RegionId) =>
  _getVersionStacks(state.versions, regionId)

export const getVersionWhitelist = (state: ReduxState, regionId: RegionId) =>
  _getVersionWhitelist(state.versions, regionId)
